import axios from 'axios'

import { getApiErrorMessage, isTimeoutError } from '@/utils/apiErrors'

// Marks an unreadable body. The leading space makes a collision with a real API
// message improbable, not impossible.
const UNREADABLE = ' unreadable'

/**
 * Describes a failure the API did not name, for display beside the thing that failed.
 *
 * Always carries the status, because the bodies that reach here are the ones
 * nothing can read — an HTML page from a proxy, or nothing at all. Without it
 * every such failure reads the same, and a request refused before it reached the
 * service is indistinguishable from one the service rejected.
 *
 * `failed` names what did not happen, and is used only when the body says nothing
 * usable; a body the API did name speaks for itself.
 */
export const describeApiFailure = (error: unknown, failed: string): string => {
  // Ahead of the branch below, which a timeout would otherwise fall into: it
  // carries no response either, but the request may well have arrived.
  if (isTimeoutError(error)) {
    return `${failed} — the server did not answer in time.`
  }

  if (axios.isAxiosError(error) && error.response === undefined) {
    return `${failed} — the request did not reach the server.`
  }

  const status = axios.isAxiosError(error) ? error.response?.status : undefined
  const detail = getApiErrorMessage(error, UNREADABLE)

  if (detail === UNREADABLE) {
    return status === undefined ? `${failed}.` : `${failed} (HTTP ${status}).`
  }
  return status === undefined ? detail : `${detail} (HTTP ${status})`
}
